import React from 'react';
import { ArrowRight, MapPin, Shield, CheckCircle, Truck, Lock } from 'lucide-react';
import SEO from '../components/SEO';

const StorageWhitewright = () => {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'SelfStorage',
    name: 'VD Storage Bells – Serving Whitewright, TX',
    url: 'https://vdstoragebells.com/storage-whitewright-tx',
    telephone: '(903) 527-3992',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '805 S. 3rd Street',
      addressLocality: 'Bells',
      addressRegion: 'TX',
      postalCode: '75414',
      addressCountry: 'US'
    },
    description: 'Self storage near Whitewright, TX. VD Storage Bells offers affordable drive-up storage units with gated access — minutes from Whitewright in Grayson County.',
    priceRange: '$'
  };

  const benefits = [
    'Convenient drive from Whitewright via FM 902 or Hwy 82',
    'Drive-up units — pull right to your door',
    'Secure electronic gate access',
    'Month-to-month leases — flexible and no commitment',
    'Multiple unit sizes for homes and businesses',
    'Rent or pay online anytime',
    'Affordable local rates',
    'Family-owned and locally operated'
  ];

  const unitSizes = [
    { size: '5x10', ideal: 'Seasonal items, boxes, small furniture', use: 'Walk-in closet' },
    { size: '10x10', ideal: '1–2 bedroom apartment contents', use: 'Average bedroom' },
    { size: '10x15', ideal: '2–3 bedroom home contents', use: 'Large bedroom' },
    { size: '10x20', ideal: 'Full house or contractor tools', use: 'Single-car garage' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Storage Units near Whitewright, TX | VD Storage Bells"
        description="Self storage near Whitewright, TX at VD Storage Bells. Affordable drive-up units with gated access. Serving Grayson County — rent online today."
        canonical="/storage-whitewright-tx"
        structuredData={structuredData}
      />

      {/* Header */}
      <section className="bg-green-600 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4" data-testid="whitewright-title">
            Storage Units near Whitewright, TX
          </h1>
          <p className="text-xl text-green-100 max-w-3xl mx-auto">
            VD Storage Bells — affordable self storage serving Whitewright and Grayson County
          </p>
        </div>
      </section>

      {/* Intro */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xl text-gray-700 leading-relaxed mb-6">
            Looking for storage near Whitewright? VD Storage Bells is just a short drive from Whitewright on FM 902 or Hwy 82, serving residents and businesses throughout Grayson County. Our drive-up self storage units make loading and unloading easy — no stairs, no carts, just pull up to your door.
          </p>
          <p className="text-lg text-gray-700 leading-relaxed">
            Whether you're moving, downsizing, or need extra space for tools and equipment, we offer flexible month-to-month storage at affordable rates with secure gated access.
          </p>
        </div>
      </section>

      {/* Unit Sizes */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Available Unit Sizes
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Drive-up access on every unit — easy for trucks and trailers
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {unitSizes.map((unit, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-md text-center">
                <div className="text-3xl font-bold text-green-600 mb-2">{unit.size}</div>
                <div className="text-sm font-medium text-gray-500 mb-3">{unit.use}</div>
                <p className="text-gray-700 text-sm">{unit.ideal}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Whitewright Residents Choose VD Storage
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-gray-50 p-8 rounded-xl text-center">
              <MapPin className="h-12 w-12 text-green-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Nearby Location</h3>
              <p className="text-gray-600">Quick drive from Whitewright — the most convenient storage in the area.</p>
            </div>
            <div className="bg-gray-50 p-8 rounded-xl text-center">
              <Truck className="h-12 w-12 text-green-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Drive-Up Units</h3>
              <p className="text-gray-600">Pull right to your unit door — no hauling boxes through long hallways.</p>
            </div>
            <div className="bg-gray-50 p-8 rounded-xl text-center">
              <Shield className="h-12 w-12 text-green-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Secure & Gated</h3>
              <p className="text-gray-600">Electronic gate entry and 24/7 video surveillance to protect your belongings.</p>
            </div>
            <div className="bg-gray-50 p-8 rounded-xl text-center">
              <Lock className="h-12 w-12 text-green-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Rent Online</h3>
              <p className="text-gray-600">Rent or manage your unit online — available 24/7 from any device.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-green-50 border-t border-b border-green-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">
            Everything You Need in a Storage Facility
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-start space-x-3">
                <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-lg text-gray-700">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Directions */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Getting Here from Whitewright</h2>
          <p className="text-lg text-gray-700 leading-relaxed mb-4">
            From Whitewright, head west on FM 902 toward Bells, or take Hwy 82 West. VD Storage is located at 805 S. 3rd Street in Bells, TX. The facility is easy to find with wide driveways that accommodate large trucks and trailers.
          </p>
          <p className="text-lg text-gray-700 leading-relaxed">
            We proudly serve residents and businesses from Whitewright, Bells, Tom Bean, Savoy, and across Grayson County.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Rent near Whitewright?</h2>
          <p className="text-xl text-gray-300 mb-8">
            Check availability and reserve your unit online — simple and fast.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://vdstoragebells.mysms360.com/rent?utm_source=vdstoragebells.com&utm_content=whitewright"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-8 py-3 text-lg font-semibold text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors shadow-lg"
              data-testid="rent-now-button"
            >
              Rent Online Now
              <ArrowRight className="ml-2 h-5 w-5" />
            </a>
            <a
              href="/contact"
              className="inline-flex items-center px-8 py-3 text-lg font-semibold text-gray-900 bg-white rounded-lg hover:bg-gray-100 transition-colors shadow-lg"
              data-testid="contact-button"
            >
              Contact Us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default StorageWhitewright;
